import { useMutation } from '@tanstack/react-query';
import { useRevalidator } from 'react-router-dom';

import { useInvalidateAppQuery } from '@/hooks/useInvalidateAppQuery';
import { api } from '@/lib/api-client';
import type { MutationConfig } from '@/lib/react-query';
import { toaster } from '@/lib/toaster';

import type { PublishSandboxResponse } from '../models/publishSandbox';

interface PublishSandboxParams {
  sandboxId: string;
  gameCode: string;
}

export type { PublishSandboxResponse };

export const publishSandbox = async ({
  sandboxId,
}: PublishSandboxParams): Promise<PublishSandboxResponse> => {
  const response = await api.post<PublishSandboxResponse>(
    `/v1/sandbox/${sandboxId}/publish`,
  );
  return response.data;
};

interface UsePublishSandboxOptions {
  mutationConfig?: MutationConfig<typeof publishSandbox>;
}

export const usePublishSandbox = ({
  mutationConfig,
}: UsePublishSandboxOptions = {}) => {
  const invalidateAppQuery = useInvalidateAppQuery();
  const { revalidate } = useRevalidator();
  const { onSuccess, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    mutationFn: publishSandbox,
    onSuccess: async (...args) => {
      // refresh only the current game|sandbox scoped queries (incl. the snapshot detail)
      await invalidateAppQuery({ queryKey: [], refetchType: 'all' });
      toaster.success('Sandbox published successfully');
      revalidate(); // re-run the loader so an open snapshot form reflects the new state
      onSuccess?.(...args);
    },
    ...restConfig,
  });
};
